import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { Box, ListItemButton, Tooltip, Typography } from '@mui/material'
import { MouseEvent } from 'react'

import { LANGUAGE_KEY } from '@/context/Locale/Locale'
import { useAppContext } from '@/hooks'

import { BranchRecord } from '../types'

export type BranchTreeNode = BranchRecord & { children: BranchTreeNode[] }

export const branchStatusColor: Record<
	BranchRecord['status'],
	{ background: string; color: string }
> = {
	ACTIVE: { background: '#E6F4EA', color: '#0F9D58' },
	ARCHIVED: { background: '#ECECEC', color: '#7A7A7A' },
	DELETED: { background: '#FDECEA', color: '#C62828' },
	LOCKED: { background: '#FEF2DC', color: '#C26401' },
}

const branchStatusLabel: Record<BranchRecord['status'], LANGUAGE_KEY> = {
	ACTIVE: 'BRANCHES_OPENED',
	ARCHIVED: 'BRANCHES_STATUS_ARCHIVED',
	DELETED: 'BRANCHES_STATUS_DELETED',
	LOCKED: 'BRANCHES_STATUS_LOCKED',
}

type BranchTreeItemProps = {
	activeBranchId: string | null
	branch: BranchTreeNode
	branchId: string | null
	depth?: number
	dimmedIds: ReadonlySet<string>
	expandedIds: ReadonlySet<string>
	onOpenMenu: (anchor: HTMLElement, branch: BranchTreeNode) => void
	onSelectBranch: (branchId: string) => void
	onToggleExpanded: (branchId: string) => void
}

export const BranchTreeItem = ({
	activeBranchId,
	branch,
	branchId,
	depth = 0,
	dimmedIds,
	expandedIds,
	onOpenMenu,
	onSelectBranch,
	onToggleExpanded,
}: BranchTreeItemProps) => {
	const { t } = useAppContext()
	const open = expandedIds.has(branch.id)
	const dimmed = dimmedIds.has(branch.id)
	const hasChildren = branch.children.length > 0
	const selected = branchId === branch.id
	const isActive = activeBranchId === branch.id
	const status = branchStatusColor[branch.status]
	const padLeft = 1 + depth * 1.25
	let nameColor = '#3D3D3D'
	if (dimmed) nameColor = '#9A9A9A'
	else if (selected) nameColor = '#1F4954'
	const tagLabel = isActive
		? t('BRANCHES_STATUS_ACTIVE')
		: t(branchStatusLabel[branch.status])

	return (
		<>
			<ListItemButton
				onClick={() => onSelectBranch(branch.id)}
				onContextMenu={(event: MouseEvent<HTMLElement>) => {
					event.preventDefault()
					onOpenMenu(event.currentTarget, branch)
				}}
				data-testid={`branch-tree-item-${branch.id}`}
				sx={{
					gap: 0.75,
					pl: padLeft,
					pr: 0.75,
					py: 0.5,
					minHeight: 30,
					borderRadius: '6px',
					mb: 0.25,
					opacity: dimmed ? 0.45 : 1,
					bgcolor: selected ? 'rgba(31,73,84,0.10)' : 'transparent',
					'&:hover': {
						bgcolor: selected ? 'rgba(31,73,84,0.12)' : 'rgba(31,73,84,0.06)',
					},
					'&:hover .mc-br-more': { opacity: 1 },
				}}
			>
				<Box
					onClick={(event) => {
						if (!hasChildren) return
						event.stopPropagation()
						onToggleExpanded(branch.id)
					}}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 14,
						height: 14,
						color: '#9A9A9A',
					}}
				>
					{hasChildren ? (
						open ? (
							<KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14 }} />
						) : (
							<KeyboardArrowRightOutlinedIcon sx={{ fontSize: 14 }} />
						)
					) : null}
				</Box>
				<AccountTreeOutlinedIcon sx={{ fontSize: 15, color: '#52666C' }} />
				<Typography
					sx={{
						flex: 1,
						minWidth: 0,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						fontSize: 12.5,
						fontWeight: selected ? 700 : 500,
						color: nameColor,
					}}
				>
					{branch.name}
				</Typography>
				{branch.protectedBranch ? (
					<Tooltip title={t('BRANCHES_PROTECTED')} arrow>
						<ShieldOutlinedIcon sx={{ fontSize: 12, color: '#5B43C4' }} />
					</Tooltip>
				) : null}
				{branch.restrictToExplicitPermissions ? (
					<Tooltip title={t('BRANCHES_CONFIDENTIAL_BADGE')} arrow>
						<VisibilityOffOutlinedIcon sx={{ fontSize: 12, color: '#9A9A9A' }} />
					</Tooltip>
				) : null}
				{branch.status === 'LOCKED' && (
					<LockOutlinedIcon sx={{ fontSize: 12, color: '#C26401' }} />
				)}
				<Box
					component="span"
					sx={{
						flexShrink: 0,
						fontSize: 8.5,
						fontWeight: 700,
						letterSpacing: '0.03em',
						textTransform: 'uppercase',
						color: isActive ? '#0F9D58' : status.color,
						bgcolor: isActive ? '#E6F4EA' : status.background,
						borderRadius: '4px',
						px: '5px',
						py: '2px',
					}}
				>
					{tagLabel}
				</Box>
				<Box
					className="mc-br-more"
					onClick={(event) => {
						event.stopPropagation()
						onOpenMenu(event.currentTarget, branch)
					}}
					data-testid={`branch-tree-menu-${branch.id}`}
					sx={{
						display: 'flex',
						color: '#9A9A9A',
						p: '2px',
						borderRadius: '5px',
						opacity: 0,
						transition: 'opacity .12s',
						'&:hover': { bgcolor: 'rgba(31,73,84,0.10)', color: '#1F4954' },
					}}
				>
					<MoreHorizOutlinedIcon sx={{ fontSize: 14 }} />
				</Box>
			</ListItemButton>
			{hasChildren && open
				? branch.children.map((child) => (
						<BranchTreeItem
							key={child.id}
							activeBranchId={activeBranchId}
							branch={child}
							branchId={branchId}
							depth={depth + 1}
							dimmedIds={dimmedIds}
							expandedIds={expandedIds}
							onOpenMenu={onOpenMenu}
							onSelectBranch={onSelectBranch}
							onToggleExpanded={onToggleExpanded}
						/>
					))
				: null}
		</>
	)
}
